/**
 * ← → turn the book in front of the reader, wherever focus is (PRO-4397): the large view when it
 * is open, else the purchase panel, else the section's book while at least half of it is on
 * screen. Fields keep their arrow keys, and so do shortcuts with Cmd, Ctrl or Alt.
 */

export interface KeyTarget {
  /** Whether this book is in front of the reader now. */
  active: () => boolean;
  /** -1 or 1: turn one page. */
  step: (delta: -1 | 1) => void;
}

/** Highest first: the large view, the panel, the section. */
export const KEY_ORDER = { panel: 2, section: 1, viewer: 3 } as const;

const targets = new Map<KeyTarget, number>();
let listening = false;

function onKey(e: KeyboardEvent) {
  if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") {
    return;
  }
  const field = (e.target as Element | null)?.closest?.(
    "input, textarea, select, [contenteditable]"
  );
  if (e.altKey || e.ctrlKey || e.metaKey || field) {
    return;
  }
  // Highest order first; among equals, the one added last.
  const target = Array.from(targets.entries())
    .reverse()
    .sort((a, b) => b[1] - a[1])
    .find(([t]) => t.active())?.[0];
  if (!target) {
    return;
  }
  e.preventDefault();
  target.step(e.key === "ArrowRight" ? 1 : -1);
}

/** Returns the function that removes it again. */
export function addKeyTarget(target: KeyTarget, order: number): () => void {
  targets.set(target, order);
  if (!listening) {
    window.addEventListener("keydown", onKey);
    listening = true;
  }
  return () => {
    targets.delete(target);
    if (!targets.size && listening) {
      window.removeEventListener("keydown", onKey);
      listening = false;
    }
  };
}

/** How much of `el` is inside the window, 0..1. */
export function shareOnScreen(el: Element | null): number {
  const r = el?.getBoundingClientRect();
  if (!r?.height) {
    return 0;
  }
  const seen = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
  return Math.max(0, seen) / r.height;
}
